import { fetchAll } from '@lumapps/base-api';
import type { QueryClient } from '@lumapps/base-api/react-query';
import { getSupersetFieldsFromCache } from '@lumapps/base-api/utils/getSupersetFieldsFromCache';
import { hasFields } from '@lumapps/utils/object/hasFields';
import { ONE_MINUTE } from '@lumapps/utils/time/constants';
import type { Fields } from '@lumapps/utils/types/Fields';

import { DEFAULT_INSTANCE_LIST_PARAMS, FETCH_ALL_METADATA_MAX_RESULTS } from '../constants';
import { type Metadata, type QueryMetadataListOptions, type QueryMetadataOptions } from '../types';
import { InstanceListMetadataRequest, listInstanceMetadata } from './instanceMetadataApi';
import { getMetadataBatched, listMetadata, ListMetadataRequest } from './metadataApi';

export const metadataQueryKeys = {
    all: () => ['metadata'] as const,
    getWithoutFields: (id: string) => [...metadataQueryKeys.all(), 'get', id] as const,
    get: (id: string, fields: Fields<Metadata>) => [...metadataQueryKeys.getWithoutFields(id), fields] as const,
    list: (source: QueryMetadataListOptions['source'], params: InstanceListMetadataRequest | ListMetadataRequest) => [
        ...metadataQueryKeys.all(),
        'list',
        source,
        params,
    ],
};

/** Get metadata query */
export const getQueryMetadata = (
    queryClient: QueryClient,
    metadata: string | Partial<Metadata> | undefined = undefined,
    { fields: baseFields = [], enabled = true }: QueryMetadataOptions = {},
) => {
    const id = typeof metadata === 'string' ? metadata : metadata?.id || '';

    const initialData = hasFields(metadata, baseFields) ? metadata : undefined;

    const fields = initialData
        ? baseFields
        : // We try to re-use previously requested fields to avoid refetching when we might already have the data
          getSupersetFieldsFromCache(queryClient, metadataQueryKeys.get(id, baseFields));

    return {
        queryKey: metadataQueryKeys.get(id, fields),
        queryFn: ({ signal }: { signal?: AbortSignal }) => getMetadataBatched(id, fields, signal),
        staleTime: 5 * ONE_MINUTE,
        enabled: enabled && !!id,
        initialData,
    } as const;
};

/** Get metadata list query */
export const getQueryMetadataList = (queryClient: QueryClient, params: QueryMetadataListOptions, enabled = true) => {
    const { source, fields: fieldsArray = [], ...baseParams } = params;
    const fields = fieldsArray.length ? `items(${fieldsArray.join(',')}),cursor,more` : undefined;
    const defaultParams = source === 'instance' ? DEFAULT_INSTANCE_LIST_PARAMS : undefined;
    const fetchParams = { ...defaultParams, ...baseParams, fields } as
        | InstanceListMetadataRequest
        | ListMetadataRequest;
    return {
        queryKey: metadataQueryKeys.list(source, fetchParams),
        queryFn: async () => {
            const listAPI = source === 'instance' ? listInstanceMetadata : listMetadata;
            const items = await fetchAll(listAPI, fetchParams, FETCH_ALL_METADATA_MAX_RESULTS);
            // Populate individual "get" cache entries in idle-time chunks to avoid blocking
            // the main thread when large numbers of metadata items are returned (up to 500).
            let index = 0;
            const cacheChunk = (deadline?: IdleDeadline) => {
                // Always process at least one item so we make progress even when
                // the callback fires via timeout (timeRemaining() === 0).
                while (index < items.length) {
                    queryClient.setQueryData(metadataQueryKeys.get(items[index].id, fieldsArray), items[index]);
                    index += 1;
                    if (deadline && deadline.timeRemaining() <= 0) {
                        break;
                    }
                }
                if (index < items.length) {
                    window.requestIdleCallback?.(cacheChunk, { timeout: 1000 });
                }
            };
            if (window.requestIdleCallback) {
                window.requestIdleCallback(cacheChunk, { timeout: 1000 });
            } else {
                cacheChunk();
            }
            return items;
        },
        staleTime: 5 * ONE_MINUTE,
        enabled,
    } as const;
};
